import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';

export interface Hospital {
  id: string;
  hospital_id: string;
  name: string;
  location: string;
  hospital_type?: string;
  address?: string;
  state?: string;
  district?: string;
  city?: string;
  pincode?: string;
  status: string;
  hq_id?: string;
  hq_name?: string;
  zone_id?: string;
  zone_name?: string;
  organization_id: string;
  created_at: string;
  updated_at: string;
  programs_count?: number;
  patients_count?: number;
  total_sales?: number;
  program_ids?: string[];
}

export interface CreateHospitalRequest {
  name: string;
  location: string;
  hospital_type: string;
  address?: string;
  state?: string;
  district?: string;
  city?: string;
  pincode?: string;
  program_ids?: string[];
  hq_id?: string;
  zone_id?: string;
}

export interface UpdateHospitalRequest {
  name?: string;
  location?: string;
  hospital_type?: string;
  address?: string;
  state?: string;
  district?: string;
  city?: string;
  pincode?: string;
  program_ids?: string[];
  hq_id?: string;
  zone_id?: string;
}

export interface HospitalsResponse {
  hospitals: Hospital[];
  pagination: Pagination;
  stats: {
    total_hospitals: number;
    active_hospitals: number;
  };
}

// Hospital Overview Interfaces
export interface ProgramInfo {
  id: string;
  program_id: string;
  program_name: string;
}

export interface HospitalOverviewResponse {
  id: string;
  name: string;
  location: string;
  hospital_type: string;
  status: string;
  associated_programs: number;
  programs: ProgramInfo[];
  total_patients: number;
  active_patients: number;
  high_risk_patients: number;
  total_sales: number;
  average_order_value: number;
  programs_involved: number;
  available_locations: string[];
  available_programs: ProgramInfo[];
}

export interface AnalyticsDataPoint {
  date: string;
  value: number;
}

export interface AnalyticsGraph {
  title: string;
  data_points: AnalyticsDataPoint[];
  total: number;
}

export interface AnalyticsSummary {
  total_patients: number;
  active_patients: number;
  total_sales: number;
  average_order_value: number;
  programs_involved: number;
}

export interface HospitalAnalyticsResponse {
  hospital_id: string;
  hospital_name: string;
  member_since: string;
  summary: AnalyticsSummary;
  patients_graph: AnalyticsGraph;
  sales_graph: AnalyticsGraph;
  start_date: string;
  end_date: string;
}

// Hospital Sales Report Interfaces
export interface PurchaseItem {
  name: string;
  company: string;
  quantity: number;
  rate: number;
  discount: number;
  duration: number;
  amount: number;
  total: number;
  item_type: string;
}

export interface Purchase {
  patient_id: string;
  purchase_date: string;
  sold_by: string;
  doctor_id: string;
  hospital_id: string;
  lab_id: string;
  description: string;
  id: string;
  organization_id: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
  medicines: PurchaseItem[];
  lab_tests: PurchaseItem[];
  total_amount: number;
  total_discount: number;
  net_total: number;
}

export interface Pagination {
  current_page: number;
  total_pages: number;
  total_count: number;
  limit: number;
  has_next: boolean;
  has_prev: boolean;
}

export interface SalesStats {
  total_purchases: number;
  total_amount: number;
  total_net_amount: number;
}

export interface HospitalSalesReportResponse {
  purchases: Purchase[];
  pagination: Pagination;
  stats: SalesStats;
}

// Hospital Patients Interfaces
export interface TimelineEvent {
  date: string;
  information: string;
}

export interface TelecallerInfo {
  id: string;
  user_id: string;
  name: string;
  avatar_image: string;
}

export interface ProgramDetail {
  id: string;
  program_id: string;
  program_name: string;
  event_type: string;
  location: string;
  date: string;
}

export interface Patient {
  name: string;
  email: string;
  age: number;
  gender: string;
  contact_number: string;
  program_ids: string[];
  status: string;
  followup_status: string;
  therapy_status: string;
  risk_level: string;
  followups_completed: number;
  followups_pending: number;
  followups_rescheduled: number;
  next_followup_date: string;
  last_followup_date: string;
  next_reminder_date: string;
  therapy_start_date: string;
  completion_rate: number;
  award_points: number;
  consent_status: string;
  consent_channels: string[];
  therapy_notes: string;
  treatment_notes: string;
  diagnostics_notes: string;
  timeline: TimelineEvent[];
  id: string;
  patient_id: string;
  organization_id: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
  user_id: string;
  doctor_id: string;
  telecaller: TelecallerInfo;
  calls_count: number;
  called_by_telecallers: string[];
  programs: ProgramDetail[];
}

export interface PatientsStats {
  total_patients: number;
  active_patients: number;
  high_risk_patients: number;
  followups_due_this_week: number;
  followups_completed: number;
  followups_pending: number;
  followups_rescheduled: number;
  total_followups_done: number;
}

export interface HospitalPatientsResponse {
  patients: Patient[];
  programs: ProgramDetail[];
  telecallers: TelecallerInfo[];
  pagination: Pagination;
  stats: PatientsStats;
}

@Injectable({
  providedIn: 'root',
})
export class HospitalsApiService {
  private readonly apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  /**
   * Get hospitals with pagination and optional search
   * GET /api/v1/hospitals/
   */
  getHospitals(
    page: number = 1,
    limit: number = 10,
    search?: string,
    status?: string,
    hq?: string,
    city?: string,
    state?: string
  ): Observable<HospitalsResponse> {
    let url = `${this.apiUrl}/hospitals/?page=${page}&limit=${limit}`;
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    if (status) {
      url += `&status=${status}`;
    }
    if (hq) {
      url += `&hq=${encodeURIComponent(hq)}`;
    }
    if (city) {
      url += `&city=${encodeURIComponent(city)}`;
    }
    if (state) {
      url += `&state=${encodeURIComponent(state)}`;
    }
    return this.http.get<HospitalsResponse>(url);
  }

  /**
   * Get a specific hospital by ID
   * GET /api/v1/hospitals/{hospital_id}
   */
  getHospital(id: string): Observable<Hospital> {
    return this.http.get<Hospital>(`${this.apiUrl}/hospitals/${id}`);
  }

  /**
   * Create a new hospital
   * POST /api/v1/hospitals/
   */
  createHospital(hospitalData: CreateHospitalRequest): Observable<Hospital> {
    return this.http.post<Hospital>(`${this.apiUrl}/hospitals/`, hospitalData);
  }

  /**
   * Bulk upload hospitals via CSV/Excel
   * POST /api/v1/hospitals/bulk-upload
   */
  bulkUploadHospitals(file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<any>(`${this.apiUrl}/hospitals/bulk-upload`, formData);
  }

  /**
   * Update an existing hospital
   * PATCH /api/v1/hospitals/{hospital_id}
   */
  updateHospital(id: string, hospitalData: UpdateHospitalRequest): Observable<Hospital> {
    return this.http.patch<Hospital>(`${this.apiUrl}/hospitals/${id}`, hospitalData);
  }

  /**
   * Delete a hospital
   * DELETE /api/v1/hospitals/{hospital_id}
   */
  deleteHospital(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/hospitals/${id}`);
  }

  /**
   * Change hospital status (active/inactive)
   * PATCH /api/v1/hospitals/{hospital_id}/status
   */
  changeHospitalStatus(id: string, status: string): Observable<Hospital> {
    return this.http.patch<Hospital>(`${this.apiUrl}/hospitals/${id}/status`, { status });
  }

  /**
   * Get hospital overview with metrics and filters
   * GET /api/v1/hospitals/{hospital_id}/overview
   */
  getHospitalOverview(
    hospitalId: string,
    filters?: {
      start_date?: string;
      end_date?: string;
      location?: string;
      program_id?: string;
    }
  ): Observable<HospitalOverviewResponse> {
    let url = `${this.apiUrl}/hospitals/${hospitalId}/overview`;
    const params: string[] = [];

    if (filters) {
      if (filters.start_date) params.push(`start_date=${encodeURIComponent(filters.start_date)}`);
      if (filters.end_date) params.push(`end_date=${encodeURIComponent(filters.end_date)}`);
      if (filters.location) params.push(`location=${encodeURIComponent(filters.location)}`);
      if (filters.program_id) params.push(`program_id=${encodeURIComponent(filters.program_id)}`);
    }

    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }

    return this.http.get<HospitalOverviewResponse>(url);
  }

  /**
   * Get hospital sales report with pagination and filters
   * GET /api/v1/hospitals/{hospital_id}/sales-report
   */
  getHospitalSalesReport(
    hospitalId: string,
    filters?: {
      item_name?: string;
      start_date?: string;
      end_date?: string;
      patient_id?: string;
      last_days?: number;
      type?: string;
      page?: number;
      limit?: number;
    }
  ): Observable<HospitalSalesReportResponse> {
    let url = `${this.apiUrl}/hospitals/${hospitalId}/sales-report`;
    const params: string[] = [];

    if (filters) {
      if (filters.item_name) params.push(`item_name=${encodeURIComponent(filters.item_name)}`);
      if (filters.start_date) params.push(`start_date=${encodeURIComponent(filters.start_date)}`);
      if (filters.end_date) params.push(`end_date=${encodeURIComponent(filters.end_date)}`);
      if (filters.patient_id) params.push(`patient_id=${encodeURIComponent(filters.patient_id)}`);
      if (filters.last_days !== undefined) params.push(`last_days=${filters.last_days}`);
      if (filters.type) params.push(`type=${encodeURIComponent(filters.type)}`);
      if (filters.page !== undefined) params.push(`page=${filters.page}`);
      if (filters.limit !== undefined) params.push(`limit=${filters.limit}`);
    }

    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }

    return this.http.get<HospitalSalesReportResponse>(url);
  }

  /**
   * Get hospital patients with pagination and search
   * GET /api/v1/hospitals/{hospital_id}/patients
   */
  getHospitalPatients(
    hospitalId: string,
    search?: string,
    page: number = 1,
    limit: number = 10
  ): Observable<HospitalPatientsResponse> {
    let url = `${this.apiUrl}/hospitals/${hospitalId}/patients?page=${page}&limit=${limit}`;

    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }

    return this.http.get<HospitalPatientsResponse>(url);
  }

  /**
   * @deprecated This method doesn't exist in the API
   */
  getPrograms(arg0: number, arg1: number) {
    throw new Error('Method not implemented - use ProgramsApiService instead.');
  }

  /**
   * Get hospital analytics
   * GET /api/v1/dashboard/analytics/hospital/{hospital_id}
   */
  getHospitalAnalytics(hospitalId: string, startDate?: string, endDate?: string): Observable<HospitalAnalyticsResponse> {
    let params: any = {};
    
    if (startDate) {
      params.start_date = startDate;
    }
    if (endDate) {
      params.end_date = endDate;
    }

    return this.http.get<HospitalAnalyticsResponse>(
      `${this.apiUrl}/dashboard/analytics/hospital/${hospitalId}`,
      { params }
    );
  }
}
